import { Component, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Category, ApiResponse } from '../../_service/data.service';
import { DataRemoteService } from '../../_service/data-remote.service';
import { DataService } from '../../_service/data.service';
import { DataOfflineService } from '../../_service/data-offline.service';
import { SnackbarService } from '../../_service/snackbar.service';
import { ButtonModule } from 'primeng/button';
import { ColorPickerModule } from 'primeng/colorpicker';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TreeModule } from 'primeng/tree';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService, TreeDragDropService, TreeNode } from 'primeng/api';
import { forkJoin, from, of } from 'rxjs';
import { catchError, concatMap, finalize, map } from 'rxjs/operators';

type CategoryEditForm = {
  _id: string;
  Name: string;
  ShortName: string;
  Color: string;
  Icon: string;
  Owner: string;
  SortOrder: number | null;
};

@Component({
  selector: 'app-edit-categories',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    ColorPickerModule,
    ConfirmDialogModule,
    DialogModule,
    InputNumberModule,
    InputTextModule,
    TreeModule,
    TooltipModule,
  ],
  providers: [ConfirmationService, TreeDragDropService],
  styles: [
    `
      /* Let hover tooltips breathe outside the scroll wrapper */
      :host ::ng-deep .resizable-table .p-tree-node-content,
      :host ::ng-deep .resizable-table .p-tree-container,
      :host ::ng-deep .resizable-table .p-tree-node-children {
        overflow: visible !important;
      }

      :host ::ng-deep .resizable-table .p-tree-node-content {
        padding: 0;
        align-items: stretch;
      }

      :host ::ng-deep .resizable-table .p-tree-node-label {
        display: block;
        flex: 1 1 auto;
        min-width: 0;
        width: 100%;
      }

      :host ::ng-deep .resizable-table .p-tree-node-toggle-button,
      :host ::ng-deep .resizable-table .p-tree-node-icon {
        display: none;
      }

      :host ::ng-deep .resizable-table .p-tree-node-droppoint {
        position: relative;
        height: 2px;
        margin: 0;
        padding: 0;
      }

      :host ::ng-deep .resizable-table .p-tree-node-droppoint::before {
        content: '';
        position: absolute;
        left: 1.25rem;
        right: 1.25rem;
        top: 0;
        border-top: 1px dashed rgba(148, 163, 184, 0.7);
        opacity: 0.35;
      }

      :host ::ng-deep .resizable-table .p-tree-node-droppoint-active::before {
        opacity: 0.9;
        border-top: 4px solid #ef4444;
        animation: dropPulse 0.9s ease-in-out infinite;
      }

      :host ::ng-deep .resizable-table .p-tree-node-content.p-tree-node-dragover .tree-row {
        outline: 2px solid rgba(239, 68, 68, 0.7);
        outline-offset: -2px;
        background: rgba(239, 68, 68, 0.06);
        animation: reparentPulse 0.9s ease-in-out infinite;
      }

      @keyframes dropPulse {
        0% {
          box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.35);
        }
        70% {
          box-shadow: 0 0 0 6px rgba(239, 68, 68, 0);
        }
        100% {
          box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
        }
      }

      @keyframes reparentPulse {
        0% {
          box-shadow: inset 0 0 0 0 rgba(239, 68, 68, 0.18);
        }
        70% {
          box-shadow: inset 0 0 0 8px rgba(239, 68, 68, 0);
        }
        100% {
          box-shadow: inset 0 0 0 0 rgba(239, 68, 68, 0);
        }
      }

      /* Sharper borders between columns */
      :host ::ng-deep .resizable-table .tree-row {
        border-color: var(--p-surface-300, #e2e8f0);
        align-items: center;
        min-height: 36px;
      }

      :host ::ng-deep .resizable-table .p-tree-root-children,
      :host ::ng-deep .resizable-table .p-tree-node-children,
      :host ::ng-deep .resizable-table .p-tree-node {
        margin: 0;
        padding: 0;
      }

      :host ::ng-deep .resizable-table .tree-header-cell {
        position: relative;
        user-select: none;
        display: flex;
        align-items: center;
        min-height: 32px;
        padding-right: 1.5rem;
        border-right: 1px solid rgba(255, 255, 255, 0.2);
      }

      :host ::ng-deep .resizable-table .col-resizer {
        position: absolute;
        top: 0;
        right: -2px;
        width: 12px;
        height: 100%;
        cursor: col-resize;
        z-index: 2;
      }

      :host ::ng-deep .resizable-table .col-resizer::after {
        content: '';
        position: absolute;
        top: 6px;
        bottom: 6px;
        left: 5px;
        width: 3px;
        border-radius: 2px;
        background: rgba(255, 255, 255, 0.65);
      }

      :host ::ng-deep .resizable-table .tree-header-cell:hover .col-resizer::after {
        background: rgba(255, 255, 255, 0.95);
        box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.18);
      }

      /* Primary-colored header */
      :host ::ng-deep .resizable-table .tree-header {
        background: var(--p-primary-color);
        color: var(--p-primary-contrast-color, #fff);
        position: relative;
      }

      /* Row hover highlight */
      :host ::ng-deep .resizable-table .tree-row:hover {
        background-color: #e2e8f0;
      }
    `,
  ],
  template: `
    <div class="w-full rounded-2xl border border-slate-200 bg-white/80 p-6 pb-4 shadow-sm backdrop-blur">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 class="text-xl font-semibold text-slate-900">Edit Categories</h2>
          <p class="text-sm text-slate-500">Review your hierarchy, rename nodes, and keep things tidy.</p>
        </div>
        <div class="flex items-center rounded-2xl bg-slate-900/90 px-4 py-2 text-white shadow-inner">
          <span class="text-2xl font-semibold leading-none">{{ categories.length }}</span>
          <span class="ml-2 text-xs uppercase tracking-widest text-slate-300">total categories</span>
        </div>
      </div>

      <div class="mt-4 flex flex-wrap items-center gap-2">
        <button
          class="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:-translate-y-px hover:border-slate-300 hover:text-slate-900"
          routerLink="/home">
          <span class="material-icons text-base" aria-hidden="true">home</span>
          Home
        </button>
        <button
          class="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:-translate-y-px hover:border-slate-300 hover:text-slate-900"
          routerLink="/admin">
          <span class="material-icons text-base" aria-hidden="true">arrow_back</span>
          Back to Admin
        </button>
        <button
          class="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:-translate-y-px hover:border-slate-300 hover:text-slate-900"
          (click)="loadData()">
          <span class="material-icons text-base" aria-hidden="true">refresh</span>
          Refresh
        </button>
        <button
          class="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:-translate-y-px hover:border-slate-300 hover:text-slate-900"
          (click)="normalizeAllCategoryColors()"
          pTooltip="Normalize all category colors to #RRGGBB"
          tooltipPosition="top"
          appendTo="body">
          <span class="material-icons text-base" aria-hidden="true">palette</span>
          Normalize Colors
        </button>
        <button
          class="group relative flex items-center gap-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-2 text-sm font-medium text-white shadow-lg shadow-emerald-500/30 transition hover:-translate-y-0.5"
          (click)="addRootCategory()"
          aria-label="Add root category"
          pTooltip="Add root category"
          tooltipPosition="top"
          appendTo="body">
          <span class="material-icons text-base" aria-hidden="true">add_box</span>
          Add Root Category
        </button>
      </div>

      <div class="mt-6">
        <ng-container *ngIf="tempMessage; else dataLoaded">
          <div
            class="flex items-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-slate-50/80 px-4 py-6 text-slate-500">
            <span class="material-icons animate-spin text-slate-400" aria-hidden="true">autorenew</span>
            <span>{{ tempMessage }}</span>
          </div>
        </ng-container>

        <ng-template #dataLoaded>
          <ng-container *ngIf="categoryTree.length; else emptyState">
            <div class="mb-3 text-xs uppercase tracking-[0.2em] text-slate-400">
              Drag rows to reorder. Drop between rows to sort, or on a row to re-parent.
            </div>
            <div class="resizable-table rounded-2xl border border-slate-200 shadow-sm overflow-visible">
              <div
                class="tree-header grid text-xs font-semibold uppercase tracking-widest"
                [style.gridTemplateColumns]="gridTemplateColumns">
                <div class="tree-header-cell px-3 py-1.5 border-r border-white/20 justify-center">
                  Category
                  <span class="col-resizer" (mousedown)="startResize(0, $event)"></span>
                </div>
                <div class="tree-header-cell px-3 py-1.5 text-center border-r border-white/20 justify-center">
                  ID
                  <span class="col-resizer" (mousedown)="startResize(1, $event)"></span>
                </div>
                <div class="tree-header-cell px-3 py-1.5 text-center justify-center">Actions</div>
              </div>
              <div class="max-h-[70vh] overflow-auto">
                <p-tree
                  [value]="categoryTree"
                  [indentation]="0"
                  [draggableNodes]="true"
                  [droppableNodes]="true"
                  (onNodeDrop)="onNodeDrop($event)"
                  styleClass="resizable-table text-sm text-slate-600">
                  <ng-template let-node pTemplate="default">
                    <div
                      class="tree-row grid border-t border-slate-200/80"
                      [style.gridTemplateColumns]="gridTemplateColumns">
                      <div
                        class="px-3 h-full text-sm text-slate-900 border-r border-slate-200/80 flex items-center"
                        [style.backgroundColor]="getCategoryColor(node.data)">
                        <div class="flex items-center gap-1" [style.margin-left.px]="getIndentation(node.data)">
                          <button
                            *ngIf="node.children?.length"
                            type="button"
                            class="inline-flex h-5 w-5 items-center justify-center rounded-md text-slate-500 transition hover:bg-white/60 hover:text-slate-800"
                            (click)="toggleNode(node, $event)"
                            [attr.aria-expanded]="!!node.expanded"
                            aria-label="Toggle category">
                            <span class="material-icons text-base" aria-hidden="true">{{
                              node.expanded ? 'expand_more' : 'chevron_right'
                            }}</span>
                          </button>
                          <span class="block">{{ node.data?.Name || node.label }}</span>
                        </div>
                      </div>
                      <div class="px-3 h-full border-r border-slate-200/80 flex items-center">
                        <div class="flex min-w-0 flex-1 items-center justify-between gap-2">
                          <span
                            class="block min-w-0 truncate text-left font-mono text-xs text-slate-600"
                            [title]="node.key">
                            {{ node.key }}
                          </span>
                          <button
                            type="button"
                            class="ml-2 inline-flex h-6 w-6 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-200 hover:text-slate-700"
                            (click)="copyToClipboard(node.key)"
                            pTooltip="Copy category ID"
                            tooltipPosition="top"
                            appendTo="body"
                            aria-label="Copy category ID"
                            title="Copy category ID">
                            <span class="material-icons text-sm" aria-hidden="true">content_copy</span>
                          </button>
                        </div>
                      </div>
                      <div class="px-3 h-full flex items-center">
                        <div class="flex flex-1 flex-wrap justify-center gap-1">
                          <button
                            class="group relative flex h-6 w-6 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:-translate-y-0.5 hover:border-blue-200 hover:text-blue-600"
                            (click)="editCategory(node.data)"
                            pTooltip="Edit"
                            tooltipPosition="top"
                            appendTo="body"
                            aria-label="Edit category"
                            title="Edit category">
                            <span class="material-icons text-sm" aria-hidden="true">edit</span>
                          </button>
                          <button
                            class="group relative flex h-6 w-6 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:-translate-y-0.5 hover:border-emerald-200 hover:text-emerald-600"
                            (click)="addChildCategory(node.data)"
                            pTooltip="Add child category"
                            tooltipPosition="top"
                            appendTo="body"
                            aria-label="Add child category"
                            title="Add child category">
                            <span class="material-icons text-sm" aria-hidden="true">add_circle</span>
                          </button>
                          <button
                            class="group relative flex h-6 w-6 items-center justify-center rounded-lg border border-rose-200 bg-rose-50 text-rose-600 transition hover:-translate-y-0.5 hover:border-rose-300 hover:bg-rose-100 hover:text-rose-700"
                            (click)="deleteCategory(node.data)"
                            pTooltip="Delete"
                            tooltipPosition="top"
                            appendTo="body"
                            aria-label="Delete category"
                            title="Delete category">
                            <span class="material-icons text-sm" aria-hidden="true">delete</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </ng-template>
                </p-tree>
              </div>
            </div>
          </ng-container>
        </ng-template>

        <ng-template #emptyState>
          <div
            class="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-gradient-to-br from-slate-50 via-white to-slate-50 px-6 py-10 text-center text-slate-500">
            <span class="material-icons text-4xl text-slate-400" aria-hidden="true">category</span>
            <div>
              <p class="text-base font-semibold text-slate-700">No categories found</p>
              <p class="text-sm">Create your first root category to start organizing content.</p>
            </div>
            <button
              class="rounded-lg bg-slate-900 px-5 py-2 text-sm font-semibold text-white transition hover:-translate-y-0.5"
              (click)="addRootCategory()">
              Add root category
            </button>
          </div>
        </ng-template>
      </div>
    </div>

    <p-dialog
      [(visible)]="editDialogVisible"
      (onHide)="resetEditDialog()"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: 'min(720px, 95vw)' }"
      [header]="getEditDialogHeader()"
      appendTo="body">
      <div class="flex flex-col gap-5">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div class="flex flex-col gap-2 md:col-span-2">
            <label class="text-xs font-semibold uppercase tracking-wide text-slate-500" for="categoryId">ID</label>
            <input
              id="categoryId"
              pInputText
              class="w-full text-xs font-mono"
              [ngModel]="editForm._id"
              readonly
              aria-readonly="true" />
          </div>

          <div class="flex flex-col gap-2 md:col-span-2">
            <label class="text-xs font-semibold uppercase tracking-wide text-slate-500" for="categoryName">Name</label>
            <input id="categoryName" pInputText class="w-full" [(ngModel)]="editForm.Name" autocomplete="off" />
          </div>

          <div class="flex flex-col gap-2">
            <label class="text-xs font-semibold uppercase tracking-wide text-slate-500" for="categoryShortName"
              >Short Name</label
            >
            <input
              id="categoryShortName"
              pInputText
              class="w-full"
              [(ngModel)]="editForm.ShortName"
              autocomplete="off" />
          </div>

          <div class="flex flex-col gap-2">
            <label class="text-xs font-semibold uppercase tracking-wide text-slate-500" for="categoryColor"
              >Color</label
            >
            <div class="flex items-center gap-3">
              <p-colorPicker
                inputId="categoryColor"
                format="hex"
                [(ngModel)]="editForm.Color"
                (onChange)="setColorFromInput($event.value)"></p-colorPicker>
              <input
                pInputText
                class="w-full font-mono text-sm"
                [ngModel]="getColorHex()"
                (ngModelChange)="setColorFromInput($event)"
                autocomplete="off" />
              <button
                type="button"
                class="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-slate-300 hover:text-slate-700 disabled:cursor-not-allowed disabled:text-slate-300"
                (click)="copyToClipboard(getColorHex())"
                [disabled]="!getColorHex()"
                pTooltip="Copy color"
                tooltipPosition="top"
                appendTo="body"
                aria-label="Copy color">
                <span class="material-icons text-sm" aria-hidden="true">content_copy</span>
              </button>
              <span class="text-xs font-semibold uppercase tracking-wide text-slate-500">{{ getColorHex() || '#' }}</span>
            </div>
          </div>

          <div class="flex flex-col gap-2">
            <label class="text-xs font-semibold uppercase tracking-wide text-slate-500" for="categoryIcon">Icon</label>
            <input id="categoryIcon" pInputText class="w-full" [(ngModel)]="editForm.Icon" autocomplete="off" />
          </div>

          <div class="flex flex-col gap-2">
            <label class="text-xs font-semibold uppercase tracking-wide text-slate-500" for="categoryOwner"
              >Owner</label
            >
            <input id="categoryOwner" pInputText class="w-full" [(ngModel)]="editForm.Owner" autocomplete="off" />
          </div>

          <div class="flex flex-col gap-2 md:col-span-2">
            <label class="text-xs font-semibold uppercase tracking-wide text-slate-500" for="categorySortOrder"
              >Sort Order</label
            >
            <p-inputNumber
              inputId="categorySortOrder"
              class="w-full"
              [(ngModel)]="editForm.SortOrder"
              [useGrouping]="false"
              [min]="0"></p-inputNumber>
          </div>
        </div>

        <div class="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            class="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
            (click)="resetEditDialog()">
            Cancel
          </button>
          <p-button
            label="Save changes"
            icon="pi pi-check"
            styleClass="p-button-rounded p-button-sm"
            (onClick)="saveCategoryEdits()"></p-button>
        </div>
      </div>
    </p-dialog>
    <p-confirmDialog appendTo="body"></p-confirmDialog>
  `,
})
export class EditCategoriesComponent implements OnInit {
  categories: Category[] = [];
  categoryTree: TreeNode[] = [];
  columnWidths: Array<number | string> = [260, 240, 180];
  tempMessage: string = 'Loading categories...';
  editDialogVisible = false;
  editForm: CategoryEditForm = {
    _id: '',
    Name: '',
    ShortName: '',
    Color: '',
    Icon: '',
    Owner: '',
    SortOrder: null,
  };
  private editingCategory: Category | null = null;
  private localCategoryExtras = new Map<string, Partial<Category>>();
  private isReordering = false;

  constructor(
    private dataRemoteService: DataRemoteService,
    private dataOfflineService: DataOfflineService,
    private dataService: DataService,
    private snackbarService: SnackbarService,
    private confirmationService: ConfirmationService,
  ) {}

  get gridTemplateColumns(): string {
    return this.columnWidths
      .map((width) => (typeof width === 'number' ? `${width}px` : String(width)))
      .join(' ');
  }

  // ##############################################################################
  // INITIATION & DATA LOADING FUNCTIONS
  // ##############################################################################

  ngOnInit(): void {
    console.log('######In Edit-Categories Component -- ngOnInit()###########');
    //this.loadingCounter = 0;  // Start loading
    this.loadData();
  }

  loadData(): void {
    console.log('Edit-Categories component: in loadData()');

    this.tempMessage = 'Loading categories...';

    this.dataRemoteService.getAllCategories().subscribe({
      next: (categories) => {
        this.categories = categories ? [...categories] : [];
        this.applyLocalCategoryExtras();
        this.categoryTree = this.buildCategoryTree(this.categories);
        this.tempMessage = '';
        console.log(`✅ Loaded ${this.categories.length} categories (remote)`);
        this.dataOfflineService
          .syncCategoriesFromCouchDB(this.categories)
          .then(() => this.dataService.refreshLocalData())
          .catch((err) => {
            console.error('❌ Failed to sync categories to IndexedDB:', err);
          });
      },
      error: (err) => {
        console.error('❌ Error loading categories:', err);
        this.tempMessage = 'Unexpected error while loading categories.';
      },
    });
  }

  trackByCategoryId(_index: number, category: Category): string {
    return category._id ?? `${_index}`;
  }

  getCategoryColor(category: Category | null | undefined): string {
    if (!category) {
      return 'transparent';
    }

    if (category.Color) {
      return this.formatHexColor(category.Color);
    }

    if (category.Parent) {
      const parent = this.categories.find((item) => item._id === category.Parent);
      if (parent?.Color) {
        return this.formatHexColor(parent.Color);
      }
    }

    return 'transparent';
  }

  /** Opens an edit dialog for the selected category */
  editCategory(category: Category) {
    this.editingCategory = category;
    this.editForm = {
      _id: category._id ?? '',
      Name: category.Name ?? '',
      ShortName: category.ShortName ?? '',
      Color: this.normalizeHexValue(category.Color),
      Icon: category.Icon ?? '',
      Owner: category.Owner ?? '',
      SortOrder: category.SortOrder ?? null,
    };
    this.editDialogVisible = true;
  }

  saveCategoryEdits() {
    if (!this.editingCategory) {
      return;
    }

    const trimmedName = this.editForm.Name.trim();
    if (!trimmedName) {
      this.snackbarService.showErrorMessage('Category name is required.');
      return;
    }

    const updatedCategory: Partial<Category> & { _id: string } = {
      _id: this.editingCategory._id,
      Name: trimmedName,
      ShortName: this.normalizeOptionalString(this.editForm.ShortName),
      Color: this.normalizeOptionalString(this.getColorHex()),
      Icon: this.normalizeOptionalString(this.editForm.Icon),
      Owner: this.normalizeOptionalString(this.editForm.Owner),
      SortOrder: this.editForm.SortOrder ?? undefined,
      Parent: this.editingCategory.Parent ?? null,
    };

    this.dataRemoteService.updateCategory(updatedCategory).subscribe({
      next: (resp: ApiResponse<Category>) => {
        if (resp.data) {
          const expandedKeys = this.captureExpandedKeys(this.categoryTree);
          this.localCategoryExtras.set(this.editingCategory!._id, {
            ShortName: updatedCategory.ShortName,
            Color: updatedCategory.Color,
            Icon: updatedCategory.Icon,
            Owner: updatedCategory.Owner,
            SortOrder: updatedCategory.SortOrder,
          });
          const index = this.categories.findIndex((category) => category._id === this.editingCategory!._id);
          if (index >= 0) {
            this.categories[index] = { ...this.categories[index], ...resp.data };
          }
          this.applyLocalCategoryExtras();
          this.categoryTree = this.buildCategoryTree(this.categories);
          this.applyExpandedKeys(this.categoryTree, expandedKeys);
          this.snackbarService.showSuccessMessage('Successfully updated category!');
          this.resetEditDialog();
        } else {
          this.snackbarService.showErrorMessage('Server did not return updated category.');
        }
      },
      error: (err: any) => {
        this.snackbarService.showErrorMessage('Could not update category.');
        console.error('Could not update category.', err);
      },
    });
  }

  resetEditDialog() {
    this.editDialogVisible = false;
    this.editingCategory = null;
    this.editForm = {
      _id: '',
      Name: '',
      ShortName: '',
      Color: '',
      Icon: '',
      Owner: '',
      SortOrder: null,
    };
  }

  getColorHex(): string {
    const normalized = this.normalizeHexValue(this.editForm.Color);
    return normalized ? `#${normalized}` : '';
  }

  getEditDialogHeader(): string {
    const name = this.editForm.Name?.trim();
    return name ? `Edit Category — ${name}` : 'Edit Category';
  }

  /** Adds a child category to the selected parent */
  addChildCategory(parent: Category) {
    const newName = prompt('Enter new category name:');
    if (newName && newName.trim() !== '') {
      this.dataRemoteService
        .createCategory({
          name: newName.trim(),
          parent_id: parent._id,
        })
        .subscribe({
          next: (resp: ApiResponse<Category>) => {
            if (resp.data) {
              this.categories.push(resp.data);
              this.snackbarService.showSuccessMessage('Successfully added category!');
              this.loadData(); // reload
            } else {
              const errorMsg = 'Could not add category.';
              this.snackbarService.showErrorMessage(errorMsg);
            }
          },
          error: (err) => {
            const errorMsg = 'Could not add category.';
            this.snackbarService.showErrorMessage(errorMsg);
            console.error(errorMsg, err);
            //this.dialogService.showError(errorMsg, err);
          },
        });
    }
  }

  /** Deletes the selected category */
  deleteCategory(category: Category) {
    if (confirm(`Are you sure you want to delete "${category.Name}"?`)) {
      this.dataRemoteService.deleteCategory(category._id).subscribe({
        next: () => {
          this.categories = this.categories.filter((c) => c._id !== category._id);
          this.snackbarService.showSuccessMessage('Successfully deleted category!');
          this.loadData(); // reload

          // this.messageService.add({
          //   severity: 'success',
          //   summary: 'Deleted',
          //   detail: `Category "${category.Name}" deleted.`
          // });
        },
        error: (err) => {
          const errorMsg = 'Could not delete category.';
          this.snackbarService.showErrorMessage(errorMsg);
          console.error(errorMsg, err);
        },
      });
    }
  }

  /** Adds a new root category */
  addRootCategory() {
    const newName = prompt('Enter new root category name:');
    if (newName && newName.trim() !== '') {
      this.dataRemoteService
        .createCategory({
          name: newName.trim(),
          parent_id: null,
        })
        .subscribe({
          next: (resp: ApiResponse<Category>) => {
            if (resp.data) {
              this.categories.push(resp.data);
              this.snackbarService.showSuccessMessage('Successfully added category!');
              this.loadData(); // reload
            } else {
              const errorMsg = 'Could not add category.';
              this.snackbarService.showErrorMessage(errorMsg);
            }
          },
          error: (err) => {
            const errorMsg = 'Could not add root category.';
            this.snackbarService.showErrorMessage(errorMsg);
            console.error(errorMsg, err);
          },
        });
    }
  }

  copyToClipboard(value?: string | null) {
    if (!value) {
      this.snackbarService.showErrorMessage('No ID available to copy.');
      return;
    }

    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(value)
        .then(() => this.snackbarService.showSuccessMessage('Copied to clipboard'))
        .catch(() => this.fallbackCopy(value));
      return;
    }

    this.fallbackCopy(value);
  }

  private fallbackCopy(value: string) {
    if (typeof document === 'undefined') {
      this.snackbarService.showErrorMessage('Clipboard not supported in this environment.');
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
      const success = document.execCommand('copy');
      if (success) {
        this.snackbarService.showSuccessMessage('Copied to clipboard');
      } else {
        throw new Error('execCommand returned false');
      }
    } catch (error) {
      console.error('Failed to copy to clipboard', error);
      this.snackbarService.showErrorMessage('Failed to copy ID');
    } finally {
      document.body.removeChild(textarea);
    }
  }

  /** Move category to a new parent */
  moveCategory(category: Category) {
    const newParentId = prompt('Enter new parent category _id (leave empty for root):', category.Parent ?? '');
    if (newParentId !== null) {
      const payload: Partial<any> & { _id: string } = {
        // Update typing -- now "any" because of data model inconsistencies with Angular/CouchDB
        _id: category._id,
        parent_id: newParentId.trim() === '' ? null : newParentId.trim(),
      };

      this.dataRemoteService.updateCategory(payload).subscribe({
        next: (resp: ApiResponse<Category>) => {
          if (resp.data) {
            // Update local object
            category.Parent = resp.data.Parent;
            category.LeftValue = resp.data.LeftValue;
            category.RightValue = resp.data.RightValue;
            this.snackbarService.showSuccessMessage('Successfully moved category!');
            this.loadData(); // reload
          } else {
            this.snackbarService.showErrorMessage('Server did not return updated category.');
          }
        },
        error: (err: any) => {
          this.snackbarService.showErrorMessage('Could not move category.');
        },
      });
    }
  }

  /** Calculates indentation based on category Path */
  getIndentation(category: Category | null | undefined): number {
    if (!category?.Path) {
      return 0;
    }
    return (category.Path.match(/\//g) || []).length * 20 + 5;
  }

  private applyLocalCategoryExtras() {
    for (const category of this.categories) {
      const extras = this.localCategoryExtras.get(category._id);
      if (extras) {
        Object.assign(category, extras);
      }
    }
  }

  normalizeAllCategoryColors() {
    const expandedKeys = this.captureExpandedKeys(this.categoryTree);
    const updates = this.categories
      .map((category) => {
        const normalized = this.normalizeHexValue(category.Color);
        const normalizedWithHash = normalized ? `#${normalized}` : '';
        const current = category.Color ?? '';
        if (!normalized || current === normalizedWithHash) {
          return null;
        }
        return { _id: category._id, Color: normalizedWithHash };
      })
      .filter((update): update is { _id: string; Color: string } => !!update);

    if (!updates.length) {
      this.snackbarService.showSuccessMessage('No color values needed normalization.');
      return;
    }

    const batchSize = 10;
    const batches: Array<Array<{ _id: string; Color: string }>> = [];
    for (let i = 0; i < updates.length; i += batchSize) {
      batches.push(updates.slice(i, i + batchSize));
    }

    let updatedCount = 0;
    let failedCount = 0;

    from(batches)
      .pipe(
        concatMap((batch) =>
          forkJoin(
            batch.map((update) =>
              this.dataRemoteService.updateCategory(update).pipe(
                map((resp) => ({ update, ok: !!resp?.data })),
                catchError(() => of({ update, ok: false })),
              ),
            ),
          ).pipe(
            map((results) => {
              for (const result of results) {
                if (result.ok) {
                  updatedCount += 1;
                  const index = this.categories.findIndex((category) => category._id === result.update._id);
                  if (index >= 0) {
                    this.categories[index] = { ...this.categories[index], Color: result.update.Color };
                  }
                } else {
                  failedCount += 1;
                }
              }
            }),
          ),
        ),
        finalize(() => {
          this.categoryTree = this.buildCategoryTree(this.categories);
          this.applyExpandedKeys(this.categoryTree, expandedKeys);
          if (failedCount) {
            this.snackbarService.showErrorMessage(
              `Normalized ${updatedCount} colors, ${failedCount} failed (try again).`,
            );
          } else {
            this.snackbarService.showSuccessMessage('Normalized category colors.');
          }
        }),
      )
      .subscribe({
        error: () => {
          this.snackbarService.showErrorMessage('Could not normalize category colors.');
        },
      });
  }

  private captureExpandedKeys(nodes: TreeNode[]): Set<string> {
    const keys = new Set<string>();
    const walk = (items: TreeNode[]) => {
      for (const node of items) {
        if (node.expanded && node.key) {
          keys.add(String(node.key));
        }
        if (node.children?.length) {
          walk(node.children);
        }
      }
    };
    walk(nodes);
    return keys;
  }

  private applyExpandedKeys(nodes: TreeNode[], expandedKeys: Set<string>) {
    const walk = (items: TreeNode[]) => {
      for (const node of items) {
        node.expanded = node.key ? expandedKeys.has(String(node.key)) : false;
        if (node.children?.length) {
          walk(node.children);
        }
      }
    };
    walk(nodes);
  }

  toggleNode(node: TreeNode, event: MouseEvent) {
    event.stopPropagation();
    node.expanded = !node.expanded;
  }

  private buildCategoryTree(categories: Category[]): TreeNode[] {
    const nodeMap = new Map<string, TreeNode>();
    const roots: TreeNode[] = [];

    for (const category of categories) {
      nodeMap.set(category._id, {
        key: category._id,
        label: category.Name,
        data: category,
      });
    }

    for (const category of categories) {
      const node = nodeMap.get(category._id);
      if (!node) continue;
      if (category.Parent) {
        const parentNode = nodeMap.get(category.Parent);
        if (parentNode) {
          parentNode.children ??= [];
          parentNode.children.push(node);
        } else {
          roots.push(node);
        }
      } else {
        roots.push(node);
      }
    }

    const sortNodes = (nodes: TreeNode[]) => {
      nodes.sort((a, b) => {
        const aOrder = (a.data as Category | undefined)?.SortOrder;
        const bOrder = (b.data as Category | undefined)?.SortOrder;
        const aHasOrder = typeof aOrder === 'number';
        const bHasOrder = typeof bOrder === 'number';
        if (aHasOrder && bHasOrder && aOrder !== bOrder) {
          return aOrder - bOrder;
        }
        if (aHasOrder !== bHasOrder) {
          return aHasOrder ? -1 : 1;
        }
        return (a.label ?? '').localeCompare(b.label ?? '', undefined, { sensitivity: 'base' });
      });

      for (const node of nodes) {
        if (node.children?.length) {
          sortNodes(node.children);
        } else {
          node.leaf = true;
        }
      }
    };

    sortNodes(roots);
    return roots;
  }

  private buildParentMap(): Map<string, string | null> {
    const map = new Map<string, string | null>();
    for (const category of this.categories) {
      map.set(category._id, this.normalizeParentId(category.Parent));
    }
    return map;
  }

  private isDescendant(parentMap: Map<string, string | null>, ancestorId: string, nodeId: string | null): boolean {
    let current = nodeId;
    while (current) {
      if (current === ancestorId) return true;
      current = parentMap.get(current) ?? null;
    }
    return false;
  }

  onNodeDrop(event: any) {
    if (this.isReordering) {
      this.snackbarService.showErrorMessage('Please wait for the current reorder to finish.');
      this.categoryTree = this.buildCategoryTree(this.categories);
      return;
    }

    const dragNode = event?.dragNode as TreeNode | undefined;
    if (!dragNode?.key) {
      this.categoryTree = this.buildCategoryTree(this.categories);
      return;
    }

    const parentMap = this.buildParentMap();
    const oldParentId = parentMap.get(String(dragNode.key)) ?? null;
    const foundParent = this.findParentIdFromTree(String(dragNode.key));
    if (!foundParent.found) {
      this.categoryTree = this.buildCategoryTree(this.categories);
      return;
    }
    const newParentId = foundParent.parentId;

    if (this.isDescendant(parentMap, String(dragNode.key), newParentId)) {
      this.snackbarService.showErrorMessage('Cannot move a category into its own descendant.');
      this.categoryTree = this.buildCategoryTree(this.categories);
      return;
    }

    const applyMove = () => {
      this.isReordering = true;
      const updates = this.buildReorderUpdates();
      if (!updates.length) {
        this.isReordering = false;
        return;
      }

      forkJoin(
        updates.map((update) =>
          this.dataRemoteService.updateCategory(
            {
              _id: update._id,
              SortOrder: update.sortOrder,
              ...(update.includeParent ? { parent_id: update.parentId } : {}),
            } as Partial<Category> & { _id: string },
          ),
        ),
      ).subscribe({
        next: () => {
          this.snackbarService.showSuccessMessage('Category order updated.');
          this.loadData();
          this.isReordering = false;
        },
        error: () => {
          this.snackbarService.showErrorMessage('Could not update category order.');
          this.isReordering = false;
          this.loadData();
        },
      });
    };

    if (oldParentId !== newParentId) {
      this.confirmationService.confirm({
        header: 'Confirm move',
        message: 'Move this category into the new parent?',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Move',
        rejectLabel: 'Cancel',
        accept: () => applyMove(),
        reject: () => {
          this.categoryTree = this.buildCategoryTree(this.categories);
        },
      });
      return;
    }

    applyMove();
  }

  private findParentIdFromTree(targetId: string): { found: boolean; parentId: string | null } {
    const walk = (
      nodes: TreeNode[],
      parentId: string | null,
    ): { found: boolean; parentId: string | null } | undefined => {
      for (const node of nodes) {
        if (String(node.key) === targetId) {
          return { found: true, parentId };
        }
        if (node.children?.length) {
          const match = walk(node.children, String(node.key));
          if (match) {
            return match;
          }
        }
      }
      return undefined;
    };

    return walk(this.categoryTree, null) ?? { found: false, parentId: null };
  }

  private normalizeParentId(value: string | null | undefined): string | null {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  }

  private buildReorderUpdates(): { _id: string; parentId: string | null; sortOrder: number; includeParent: boolean }[] {
    const updates: { _id: string; parentId: string | null; sortOrder: number; includeParent: boolean }[] = [];
    const byId = new Map(this.categories.map((category) => [category._id, category]));
    let globalOrder = 0;

    const walk = (nodes: TreeNode[], parentId: string | null) => {
      nodes.forEach((node, index) => {
        const category = byId.get(String(node.key));
        if (!category) return;
        const newSortOrder = globalOrder++;
        const newParentId = parentId;
        const sortOrderChanged = (category.SortOrder ?? null) !== newSortOrder;
        const parentChanged = this.normalizeParentId(category.Parent) !== newParentId;
        if (sortOrderChanged || parentChanged) {
          updates.push({
            _id: category._id,
            parentId: newParentId,
            sortOrder: newSortOrder,
            includeParent: parentChanged,
          });
          category.SortOrder = newSortOrder;
          category.Parent = newParentId;
        }

        if (node.children?.length) {
          walk(node.children, category._id);
        }
      });
    };

    walk(this.categoryTree, null);
    return updates;
  }

  private normalizeHexValue(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    return value
      .replace(/[^0-9a-fA-F]/g, '')
      .toUpperCase()
      .slice(0, 6);
  }

  private formatHexColor(value: string): string {
    const normalized = this.normalizeHexValue(value);
    return normalized ? `#${normalized}` : 'transparent';
  }

  setColorFromInput(value: unknown) {
    if (value && typeof value === 'object' && 'value' in value) {
      const extracted = (value as { value?: string }).value;
      this.editForm.Color = this.normalizeHexValue(extracted);
      return;
    }
    this.editForm.Color = this.normalizeHexValue(value as string | null | undefined);
  }

  private normalizeOptionalString(value: string | null | undefined): string | undefined {
    const trimmed = value?.trim();
    return trimmed ? trimmed : undefined;
  }

  startResize(index: number, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    const current = this.columnWidths[index];
    if (typeof current !== 'number') {
      return;
    }
    this.resizingIndex = index;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = current;
  }

  @HostListener('document:mousemove', ['$event'])
  handleResizeMove(event: MouseEvent) {
    if (this.resizingIndex === null) {
      return;
    }
    const delta = event.clientX - this.resizeStartX;
    const nextWidth = Math.max(140, this.resizeStartWidth + delta);
    this.columnWidths[this.resizingIndex] = nextWidth;
  }

  @HostListener('document:mouseup')
  handleResizeEnd() {
    this.resizingIndex = null;
  }

  private resizingIndex: number | null = null;
  private resizeStartX = 0;
  private resizeStartWidth = 0;
}
